import countries from './countries'

export const formatDateToTimeAMPM = (date) => {
    const dateObj = new Date(date)

    //Extract hour and minutes + am/pm from date
    const hour = dateObj.getHours()
    const minutes = dateObj.getMinutes()
    const ampm = hour >= 12 ? 'pm' : 'am'

    //Format hour and minutes
    const formattedHour = hour % 12 === 0 ? 12 : hour % 12
    const formattedMinutes = minutes < 10 ? '0' + minutes : minutes

    return `${formattedHour}:${formattedMinutes} ${ampm}`
}

export const formatTime = (timeInSeconds) => {
    // Handle invalid or NaN values
    if (
        timeInSeconds === undefined ||
        timeInSeconds === null ||
        isNaN(timeInSeconds) ||
        !isFinite(timeInSeconds) ||
        timeInSeconds < 0
    ) {
        return '00:00'
    }

    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    return `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`
}

/** Format decimal128 to 2 decimal places */
export const formatDecimal128 = (decimal128) => {
    if (decimal128 == null) return '0.00'
    // Handle both Decimal128 object shape and primitive/string
    let value
    if (
        typeof decimal128 === 'object' &&
        decimal128 !== null &&
        Object.prototype.hasOwnProperty.call(decimal128, '$numberDecimal')
    ) {
        value = Number(decimal128.$numberDecimal)
    } else {
        value = Number(decimal128)
    }
    if (!Number.isFinite(value)) return '0.00'
    return value.toFixed(2)
}

/**
 * Returns a short display name for CTAs: first word only, truncated if too long.
 * @param {string} [displayname] - Full display name
 * @param {number} [maxLength=12] - Max characters for the name (no ellipsis added)
 * @returns {string}
 */
export const getShortDisplayName = (displayname, maxLength = 12) => {
    if (displayname == null || typeof displayname !== 'string') return ''
    const trimmed = displayname.trim()
    if (!trimmed) return ''
    const firstWord = trimmed.split(/\s+/)[0] || trimmed
    if (firstWord.length <= maxLength) return firstWord
    return firstWord.slice(0, maxLength)
}

/**
 * Creates a debounced function that delays invoking the provided function
 * until after the specified wait time has elapsed since the last time it was invoked.
 *
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @returns {Function} - The debounced function with a cancel method
 */
export const debounce = (func, wait = 500) => {
    let timeout

    const debounced = function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout)
            func(...args)
        }

        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
    }

    // Add cancel method to the debounced function
    debounced.cancel = () => {
        clearTimeout(timeout)
    }

    return debounced
}

// Cache for formatters to avoid recreating them for the same country
const formatterCache = new Map()
export function formatCurrency(amount, country) {
    if (!country) return ''

    // Check if we already have a formatter for this country
    if (!formatterCache.has(country)) {
        const countryMeta = countries.find((c) => c.name === country)
        if (!countryMeta) return ''

        // Create and cache the formatter
        const formatter = new Intl.NumberFormat(`es-${countryMeta.code}`, {
            style: 'currency',
            currency: countryMeta.currency_name,
            maximumFractionDigits: countryMeta.currencyFormat.decimalPlaces,
        })

        formatterCache.set(country, formatter)
    }

    // Use the cached formatter
    return formatterCache.get(country).format(amount)
}

//Export
export default {
    formatDateToTimeAMPM,
    formatTime,
    debounce,
    formatDecimal128,
    getShortDisplayName,
}
